<template>
  <gd-modal name="promotion-modal" width="100%" height="100%">
    <div ref="container" class="h-full">
      <div class="flex justify-between p-4">
        <div class="h-full font-bold text-xl text-secondary-lighter">
          {{ $t('frontend_promotion_title') }}
        </div>
        <div class="flex justify-end">
          <gd-button class="mr-4" @click="close">
            {{ $t('frontend_exit') }}
          </gd-button>
          <gd-button variant="primary" @click="addPromotion">
            {{ $t('frontend_add') }}
          </gd-button>
        </div>
      </div>
      <div ref="promotionsContainer" class="h-full flex bg-secondary-darker">
        <div class="flex flex-grow flex-wrap overflow-auto">
          <info-panel v-if="!promotions.length" class="m-4">
            <template slot="content">
              <div
                class="w-full h-full flex justify-center items-center text-lg text-secondary-darker text-center"
              >
                {{ $t('frontend_promotion_not_found') }}
              </div>
            </template>
            <template slot="footer">
              <div class="w-full h-full flex justify-center items-center">
                <gd-button variant="primary" size="lg" @click="addPromotion">
                  {{ $t('frontend_add') }}
                </gd-button>
              </div>
            </template>
          </info-panel>
          <info-panel
            v-for="promotion in promotions"
            :key="promotion.id"
            class="m-4"
          >
            <template slot="content">
              <div class="w-full h-full rounded-t-lg relative">
                <div
                  class="absolute w-full h-full flex items-center justify-center rounded-t-lg bg-grey-800 opacity-0 hover:opacity-100"
                >
                  <gd-button
                    variant="dark"
                    size="lg"
                    @click="editSite(promotion)"
                  >
                    {{ $t('frontend_sites_open_editor') }}
                  </gd-button>
                </div>
                <screenshot
                  class="w-full h-full rounded-t-lg bg-center"
                  :src="promotion.thumbnail || 'default-screenshot.png'"
                />
              </div>
            </template>
            <template slot="footer">
              <div class="w-full h-full flex justify-between items-center px-4">
                <div class="w-56 break-words">
                  {{ promotion.name }}
                </div>
                <drop-menu :menu-items="menuItems(promotion)" small />
              </div>
            </template>
          </info-panel>
        </div>
        <side-bar ref="sidebar" sections="Promotions" :title="sidebarTitle">
          <div class="m-4">
            <autosuggest
              classes="h-8 w-full px-4 bg-grey-800 text-grey-100 border border-secondary-lighter rounded-lg overflow-auto outline-none"
              placeholder="Site Name"
              :value="isSelected"
              :suggestions="suggestions"
              @onSelected="onSelected"
              @onInputChange="onInputChange"
            />
            <date-picker
              class="my-2"
              v-model="startDate"
              picker="date"
              size="small"
              dark
            />
            <date-picker
              class="my-2"
              v-model="expiryDate"
              picker="date"
              size="small"
              dark
            />
            <gd-dropdown
              class="my-2"
              v-model="priority"
              placeholder="Priority"
              :options="options"
              variant="dark"
              size="full"
            />
          </div>
          <div class="mt-5 w-full flex justify-center">
            <gd-button
              :disabled="!isValid"
              :variant="isValid ? 'primary' : 'base'"
              size="lg"
              @click="savePromotion()"
              >{{ $t('frontend_save') }}</gd-button
            >
          </div>
        </side-bar>
      </div>
    </div>
    <confirm-modal
      ref="deleteModal"
      name="delete-promotion"
      :callback="confirmDelete"
    ></confirm-modal>
  </gd-modal>
</template>

<script>
import api from '@groovepages/gd-lib/api';
import Notify from '../notify';
import Autosuggest from './Autosuggest';
import GdButton from './Button';
import ConfirmModal from './ConfirmModal';
import DatePicker from './DateTimePicker';
import DropMenu from './DropMenu';
import GdDropdown from './Dropdown';
import ImagePickerModal from './ImagePickerModal';
import InfoPanel from './InfoPanel';
import GdModal from './Modal';
import Screenshot from './Screenshot';
import SideBar from './SideBar';
import TextInput from './TextInput';

export default {
  name: 'PromotionModal',
  components: {
    GdButton,
    TextInput,
    DropMenu,
    GdDropdown,
    SideBar,
    InfoPanel,
    GdModal,
    ConfirmModal,
    ImagePickerModal,
    Screenshot,
    Autosuggest,
    DatePicker
  },
  data() {
    return {
      oneTime: 1,
      limit: 10,
      toEdit: '',
      toDelete: '',
      siteId: '',
      priority: '',
      startDate: '',
      expiryDate: '',
      promotions: [],
      isSelected: { name: '' },
      suggestions: [],
      options: [
        {
          text: 'High',
          value: 3
        },
        {
          text: 'Normal',
          value: 2
        },
        {
          text: 'Low',
          value: 1
        }
      ]
    };
  },
  computed: {
    isValid() {
      if (
        this.siteId &&
        this.startDate < this.expiryDate &&
        new Date(this.startDate).toISOString().split('T')[0] >=
          new Date().toISOString().split('T')[0]
      )
        return true;
      return false;
    },
    sidebarTitle() {
      if (!this.toEdit) return this.$t('frontend_add');
      return this.$t('frontend_edit');
    }
  },
  methods: {
    menuItems(promotion) {
      return [
        //{ text: this.$t('frontend_edit'), callback: () => this.editPromotion(promotion) },
        {
          text: this.$t('frontend_delete'),
          callback: () => this.deletePromotion(promotion)
        }
      ];
    },
    editSite(site) {
      process.env.NODE_ENV === 'development'
        ? window.location.assign(`/builder/${site.site_id}`)
        : window.location.assign(`/groovepages/builder/${site.site_id}`);
    },
    onSelected(item) {
      this.isSelected = item.item;
      this.siteId = item.item?.id;
    },
    onInputChange(item) {
      api
        .get(
          `${window.gdEnv.VUE_APP_API_URL}/groovepages/sites/list?limit=${this.limit}&search=${item}`
        )
        .then(res => {
          const data = res.sites.filter(site => site.published_at);
          this.suggestions = data.length
            ? [
                {
                  data
                }
              ]
            : [];
        })
        .catch(err => this.showError(err));
    },
    open() {
      this.initPromotions();
      this.$root.$emit('modal-open', 'promotion-modal');
    },
    close() {
      this.$root.$emit('modal-close', 'promotion-modal');
    },
    initPromotions() {
      api
        .get(`${window.gdEnv.VUE_APP_API_URL}/groovepages/promotions?active=1`)
        .then(data => {
          this.promotions = data || [];
        })
        .catch(err => this.showError(err));
    },
    addPromotion() {
      const startDate = new Date();
      const expiryDate = new Date();
      expiryDate.setDate(new Date().getDate() + 1);
      this.siteId = '';
      this.priority = '';
      this.startDate = startDate.toISOString();
      this.expiryDate = expiryDate.toISOString();
      this.isSelected = { name: '' };
      this.$refs.sidebar.open();
    },
    editPromotion(promotion) {
      this.$refs.sidebar.open();
    },
    savePromotion() {
      if (this.toEdit) {
        api
          .put(
            `${window.gdEnv.VUE_APP_API_URL}/groovedigital/promotions/${this.toEdit.id}`,
            {}
          )
          .then(data => this.showSuccess(data))
          .catch(err => this.showError(err));
      } else {
        api
          .post(`${window.gdEnv.VUE_APP_API_URL}/groovepages/promotions`, {
            site_id: this.siteId,
            start_date: new Date(this.startDate).toISOString().split('T')[0],
            end_date: new Date(this.expiryDate).toISOString().split('T')[0],
            one_time: 1,
            priority: this.priority || 0
          })
          .then(data => this.showSuccess(data))
          .catch(err => this.showError(err));
      }
      this.$refs.sidebar.close();
    },
    deletePromotion(promotion) {
      this.toDelete = promotion;
      this.$refs.deleteModal.open();
    },
    confirmDelete() {
      api
        .delete(
          `${window.gdEnv.VUE_APP_API_URL}/groovepages/promotions/${this.toDelete.id}`
        )
        .then(data => this.showSuccess(data))
        .catch(err => this.showError(err));
      this.$refs.sidebar.close();
    },
    showSuccess(data) {
      this.initPromotions();
      return Notify.success('Success');
    },
    showError(err) {
      return Notify.fail(err.message || 'Error');
    }
  }
};
</script>
